<script>
import { mapGetters, mapActions } from 'vuex';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import KanbanBoard from '@/components/crm/atendimento/KanbanBoard.vue';
import KanbanFiltros from '@/components/crm/atendimento/KanbanFiltros.vue';
import InboxList from '@/components/crm/atendimento/InboxList.vue';
import AtendimentoVisaoToggle from '@/components/crm/atendimento/AtendimentoVisaoToggle.vue';
import ChatDrawer from '@/components/crm/atendimento/ChatDrawer.vue';
import IniciarConversaFab from '@/components/crm/atendimento/IniciarConversaFab.vue';
import IniciarConversaModal from '@/components/crm/atendimento/IniciarConversaModal.vue';
import socketClient from '@/services/socketClient';
import notificationSound from '@/services/notificationSound';

const STORAGE_KEY_VIEW = 'atendimento-visao';

export default {
  name: 'AtendimentoIndex',
  components: {
    Layout,
    PageHeader,
    KanbanBoard,
    KanbanFiltros,
    InboxList,
    AtendimentoVisaoToggle,
    ChatDrawer,
    IniciarConversaFab,
    IniciarConversaModal,
  },
  data() {
    return {
      socketUnsubscribers: [],
      isMobile: false,
      visao: this.lerVisaoLocal(),
      somMutado: notificationSound.isMuted(),
      // Abertura do modal "iniciar conversa" com contato pré-selecionado
      // (ex.: vindo de um participante de grupo via query na URL).
      iniciarModal: { visivel: false, contato: null },
    };
  },
  computed: {
    ...mapGetters('atendimento', ['filtroMinhas', 'naoLidasTotal']),
    ...mapGetters('auth', ['currentUser']),
    conversaAtivaId() {
      return this.$store.state.atendimento.conversaAtivaId;
    },
    // No mobile a visão é sempre Lista (Kanban não cabe); no desktop respeita a escolha.
    visaoEfetiva() {
      return this.isMobile ? 'lista' : this.visao;
    },
    // Desktop: FAB só no Kanban (como antes). Mobile: FAB na lista, some quando a conversa abre.
    mostrarFab() {
      if (this.isMobile) return this.conversaAtivaId == null;
      return this.visao === 'kanban';
    },
  },
  watch: {
    // Title dinâmico tipo WhatsApp Web: "(3) Atendimento | PCdoB".
    // Reage a qualquer mudança no total de não-lidas (inclui webhook
    // socket que aumenta nao_lidas_count e ação marcarComoLida que zera).
    // Diferido com nextTick pra não disparar dentro do ciclo de patch
    // do Vue (evita erros de "flags" em VNode quando watcher fire
    // durante render do KanbanBoard/InboxList).
    naoLidasTotal(total) {
      this.$nextTick(() => this._atualizarTitulo(total));
    },
    // Mobile app-shell: conversa ativa vira "fullscreen" (esconde topbar/nav)
    // e o voltar nativo do celular fecha o chat em vez de trocar de rota.
    conversaAtivaId(novo, antigo) {
      if (novo != null && antigo == null) {
        document.body.classList.add('atendimento-chat');
        if (this._ehMobile()) {
          window.history.pushState({ atendimentoChat: true }, '');
          this._chatHistoryPushed = true;
        }
      } else if (novo == null && antigo != null) {
        document.body.classList.remove('atendimento-chat');
        // Fechamento pela UI (botão voltar/fechar): consome a entrada que
        // empurramos no histórico, mantendo o back nativo consistente.
        if (this._chatHistoryPushed && !this._ignoreNextPop) {
          this._chatHistoryPushed = false;
          this._ignoreNextPop = true;
          window.history.back();
        }
      }
    },
  },
  created() {
    // Back nativo do celular: fecha a conversa em vez de trocar de rota.
    // Registramos o popstate AQUI (não no mounted) de propósito: assim este
    // listener roda ANTES do listener do ChatDrawer filho (que registra no
    // mounted dele, e filho monta antes do mounted do pai). Ordem importa —
    // com o painel aberto, este handler precisa ver a classe `atendimento-painel`
    // e devolver pra conversa ANTES do ChatDrawer remover a classe.
    this._chatHistoryPushed = false;
    this._ignoreNextPop = false;
    this._boundPopState = this._onPopState.bind(this);
    window.addEventListener('popstate', this._boundPopState);
  },
  async mounted() {
    document.body.classList.add('atendimento-sem-footer');
    // Detecta mobile de forma reativa (força Lista + FAB de nova conversa).
    this._mqMobile = window.matchMedia('(max-width: 767.98px)');
    this.isMobile = this._mqMobile.matches;
    this._onMqChange = (e) => { this.isMobile = e.matches; };
    this._mqMobile.addEventListener('change', this._onMqChange);

    try { await this.fetchEstagios(); } catch (e) { console.error('[atendimento] fetchEstagios falhou:', e); }
    try { await this.fetchConversas(); } catch (e) { console.error('[atendimento] fetchConversas falhou:', e); }
    // Seta título no boot — o watcher só dispara em mudanças.
    this.$nextTick(() => this._atualizarTitulo(this.naoLidasTotal));

    if (this.currentUser && this.currentUser.id) {
      socketClient.connect(this.currentUser.id);
    } else {
      socketClient.connect();
    }
    this.socketUnsubscribers.push(
      socketClient.on('conversa:nova_mensagem', (payload) => this.socketNovaMensagem(payload)),
      socketClient.on('conversa:atualizada', (payload) => this.socketConversaAtualizada(payload)),
      socketClient.on('conversa:atribuida', (payload) => this.socketConversaAtualizada(payload)),
      socketClient.on('conversa:mensagem_status', (payload) => this.socketMensagemStatus(payload)),
      socketClient.on('conversa:mensagem_atualizada', (payload) => this.socketMensagemAtualizada(payload)),
    );

    this._abrirIniciarConversaViaQuery();
    this._abrirConversaViaQuery();
  },
  beforeUnmount() {
    document.body.classList.remove('atendimento-sem-footer');
    document.body.classList.remove('atendimento-chat');
    if (this._boundPopState) window.removeEventListener('popstate', this._boundPopState);
    if (this._mqMobile) this._mqMobile.removeEventListener('change', this._onMqChange);
    this.socketUnsubscribers.forEach((unsub) => { try { unsub(); } catch (_e) { /* noop */ } });
    this.socketUnsubscribers = [];
    // Restaura o title sem o badge ao sair da rota (router já vai
    // sobrescrever, mas evita piscar "(3) Atendimento" em outra tela).
    this._atualizarTitulo(0);
  },
  methods: {
    ...mapActions('atendimento', [
      'fetchEstagios',
      'fetchConversas',
      'setFiltroMinhas',
      'selecionarConversa',
      'socketNovaMensagem',
      'socketConversaAtualizada',
      'socketMensagemStatus',
      'socketMensagemAtualizada',
    ]),
    _ehMobile() {
      return window.matchMedia('(max-width: 767.98px)').matches;
    },
    _onPopState() {
      // Voltar nativo/navegador com uma conversa aberta → fecha a conversa.
      if (this._ignoreNextPop) { this._ignoreNextPop = false; return; }
      // Painel do contato aberto (mobile): esse "voltar" fecha o painel (tratado
      // no ChatDrawer). Não fechamos a conversa aqui — volta pra conversa.
      if (document.body.classList.contains('atendimento-painel')) return;
      if (this.conversaAtivaId != null) {
        this._chatHistoryPushed = false;
        this.selecionarConversa(null);
      }
    },
    lerVisaoLocal() {
      try {
        const v = window.localStorage.getItem(STORAGE_KEY_VIEW);
        return v === 'lista' ? 'lista' : 'kanban';
      } catch (_e) { return 'kanban'; }
    },
    setVisao(v) {
      this.visao = v;
      try { window.localStorage.setItem(STORAGE_KEY_VIEW, v); } catch (_e) { /* ignore */ }
    },
    onToggleMinhas(e) {
      this.setFiltroMinhas(e.target.checked);
    },
    /**
     * Atualiza o document.title preservando o sufixo do app ("| PCdoB").
     * Total > 0 vira prefixo "(N) "; total = 0 remove o prefixo.
     */
    _atualizarTitulo(total) {
      const atual = document.title || '';
      const semBadge = atual.replace(/^\(\d+\)\s*/, '');
      document.title = total > 0 ? `(${total}) ${semBadge}` : semBadge;
    },
    toggleSom() {
      this.somMutado = !this.somMutado;
      notificationSound.setMuted(this.somMutado);
      // Toca um som de teste ao reativar — confirma pro atendente que
      // está funcionando (e libera o AudioContext na primeira interação).
      if (!this.somMutado) notificationSound.playNova();
    },
    /**
     * Abre o modal de iniciar conversa já com o contato pré-selecionado
     * quando a URL traz ?iniciarContatoId=... (ex.: a partir de um grupo).
     * Limpa a query depois para não reabrir ao navegar/atualizar.
     */
    _abrirIniciarConversaViaQuery() {
      const id = Number(this.$route.query.iniciarContatoId);
      if (!id) return;
      this.iniciarModal = {
        visivel: true,
        contato: { id, nome: this.$route.query.iniciarContatoNome || `Contato #${id}` },
      };
      const query = { ...this.$route.query };
      delete query.iniciarContatoId;
      delete query.iniciarContatoNome;
      this.$router.replace({ query }).catch(() => {});
    },
    /**
     * Abre direto a conversa pedida na URL (?conversaId=...). É por aqui que o
     * painel de respostas de campanha leva o usuário da linha "fulano
     * respondeu" para a conversa em si. Limpa a query depois, senão voltar
     * para a tela reabriria a conversa sozinho.
     */
    _abrirConversaViaQuery() {
      const id = Number(this.$route.query.conversaId);
      if (!id) return;
      this.selecionarConversa(id);
      const query = { ...this.$route.query };
      delete query.conversaId;
      this.$router.replace({ query }).catch(() => {});
    },
    fecharIniciarModal() {
      this.iniciarModal = { visivel: false, contato: null };
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Atendimento" pageTitle="Atendimento" />

    <BCard no-body class="atendimento-card">
      <!-- ===== Header toolbar (Kanban view only ou ambas) ===== -->
      <div
        v-if="visaoEfetiva === 'kanban'"
        class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div class="d-flex align-items-center gap-2">
          <AtendimentoVisaoToggle :visao="visao" @change="setVisao" />
          <KanbanFiltros class="ms-2" />
        </div>
        <div class="d-flex align-items-center gap-3">
          <div class="form-check form-switch">
            <input
              id="toggle-minhas"
              type="checkbox"
              class="form-check-input"
              :checked="filtroMinhas"
              @change="onToggleMinhas"
            />
            <label class="form-check-label" for="toggle-minhas">Apenas minhas</label>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :title="somMutado ? 'Som de notificação: desligado (clique para ativar)' : 'Som de notificação: ligado (clique para silenciar)'"
            @click="toggleSom"
          >
            <i :class="somMutado ? 'ri-notification-off-line' : 'ri-notification-3-line'"></i>
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="fetchConversas">
            <i class="ri-refresh-line"></i> Atualizar
          </button>
        </div>
      </div>

      <!-- ===== Kanban view ===== -->
      <div v-if="visaoEfetiva === 'kanban'" class="atendimento-body">
        <KanbanBoard />
      </div>

      <!-- ===== Lista view — WhatsApp Web split layout ===== -->
      <div v-else class="wa-split-layout">
        <InboxList :visao="visaoEfetiva" @change-visao="setVisao" />
        <ChatDrawer inline />
      </div>
    </BCard>

    <!-- Modo Kanban: ChatDrawer flutuante (overlay deslizante à direita). -->
    <ChatDrawer v-if="visaoEfetiva === 'kanban'" />
    <IniciarConversaFab v-if="mostrarFab" />

    <!-- Aberto via query (?iniciarContatoId=...), ex.: a partir de um grupo. -->
    <IniciarConversaModal
      v-if="iniciarModal.visivel"
      :contato-inicial="iniciarModal.contato"
      @close="fecharIniciarModal"
    />
  </Layout>
</template>

<style scoped>
.atendimento-card {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin-bottom: 0;
}
.atendimento-body {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

/* ===== WhatsApp Web split layout ===== */
.wa-split-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #f0f2f5;
  border-radius: 0 0 0.375rem 0.375rem;
}

/* Dark mode */
[data-bs-theme='dark'] .wa-split-layout {
  background: #0b141a;
}

/* ===== Mobile: alterna lista ↔ conversa (estilo WhatsApp) ===== */
@media (max-width: 767.98px) {
  .wa-split-layout { border-radius: 0; }
  .atendimento-card { border-radius: 0; border-left: 0; border-right: 0; }
  /* Lista ocupa a largura toda. */
  .wa-split-layout :deep(.wa-sidebar) { max-width: none; min-width: 0; width: 100%; }
  /* Sem conversa: só a lista (esconde o chat inline e seu empty state). */
  .wa-split-layout :deep(.chat-drawer--inline) { display: none; }
  /* Com conversa ativa: esconde a lista e mostra a conversa em fullscreen. */
  body.atendimento-chat .wa-split-layout :deep(.wa-sidebar) { display: none; }
  body.atendimento-chat .wa-split-layout :deep(.chat-drawer--inline) { display: flex; }
}
</style>

<style>
/* ===== Modo "atendimento sem footer" ============================
   A página de atendimento ocupa exatamente o viewport — sem rolagem
   no body, qualquer rolagem fica interna (lista de mensagens, etc).
   A classe é adicionada/removida em mounted/beforeUnmount da view. */
body.atendimento-sem-footer {
  overflow: hidden;
}
body.atendimento-sem-footer .footer-pcdob,
body.atendimento-sem-footer .footer {
  display: none !important;
}
body.atendimento-sem-footer .main-content {
  height: 100vh;
  height: 100dvh; /* mobile: desconta a barra do navegador */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
body.atendimento-sem-footer .page-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
}
body.atendimento-sem-footer .page-content > .container-fluid {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
/* PageHeader fica com altura natural, o card cresce pra ocupar o resto. */
body.atendimento-sem-footer .page-content > .container-fluid > .row:not(.atendimento-row),
body.atendimento-sem-footer .page-content > .container-fluid > div:first-child {
  flex-shrink: 0;
}

/* ===== Mobile: comportamento de app nativo (estilo WhatsApp) ===== */
@media (max-width: 767.98px) {
  /* Full-bleed: remove o respiro lateral e esconde o cabeçalho de página. */
  body.atendimento-sem-footer .page-content,
  body.atendimento-sem-footer .page-content > .container-fluid {
    padding-left: 0;
    padding-right: 0;
  }
  body.atendimento-sem-footer .page-content > .container-fluid > div:first-child { display: none; }
  /* Cola o conteúdo logo abaixo da topbar (mesma altura do header de conversa). */
  body.atendimento-sem-footer .page-content { padding-top: 60px; }

  /* Conversa em fullscreen: esconde a topbar do app e cola o conteúdo no topo. */
  body.atendimento-chat #page-topbar { display: none; }
  body.atendimento-chat.atendimento-sem-footer .page-content { padding-top: 0 !important; }
}
</style>
